/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo } from "react";
import { sanitizeHtml } from "../../utils/sanitize";
import FormGroup from "../ui/form/FormGroup";
import FormError from "../ui/form/FormError";
import TextField from "../ui/form/TextField";
import Checkbox from "../ui/form/Checkbox";
import DateField from "../ui/form/DateField";
import TextareaField from "../ui/form/TextareaField";
import Select from "../ui/Select";
import NumberField from "../ui/form/NumberField";

const AdditionalField = ({ field, control, errors }) => {
  const fieldComponents = useMemo(
    () => ({
      static: ({ field }) => <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.default_value) }} />,
      text: TextField,
      checkbox: Checkbox,
      date: DateField,
      textarea: TextareaField,
      select: Select,
      digits: NumberField,
      barcode: TextField,
    }),
    []
  );

  const FieldComponent = fieldComponents[field.field_type];

  if (!FieldComponent) {
    console.warn(`Unsupported field type: ${field.field_type}`);
    return null;
  }

  const commonProps = {
    control,
    name: field.name,
    label: field.field_name,
    defaultValue: field.default_value,
    required: !field.optional,
    placeholder: field.field_name,
  };

  if (field.field_type === "static") {
    return <FieldComponent field={field} />;
  }

  if (field.field_type === "select") {
    commonProps.options = field.field_options.map((option) => ({
      value: option,
      label: option,
    }));
  }

  return (
    <FormGroup key={field.id}>
      <FieldComponent {...commonProps} />
      {errors[field.name] && <FormError name={field.name}>{errors[field.name].message}</FormError>}
    </FormGroup>
  );
};

export default AdditionalField;
